import type {
  AddTabsPayload,
  ContextObject,
  SetActiveTabPayload,
  SetTabDomainPayload,
  WorkArea,
  WorkAreaId,
  WorkAreaState,
} from '@core/core/types'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createEntityAdapter, createSlice, current } from '@reduxjs/toolkit'
import type { TabElementInfo } from 'common/types'

export const workAreaAdapter = createEntityAdapter<WorkArea>({
  selectId: workArea => workArea.id,
})

const initialState = workAreaAdapter.getInitialState<WorkAreaState>({
  activeWorkAreaId: null,
})

export const workAreaSlice = createSlice({
  name: 'workAreas',
  initialState,
  reducers: {
    addWorkAreas(state, payload: PayloadAction<WorkArea[]>) {
      workAreaAdapter.addMany(state, payload)
    },
    addTabsToWorkArea(state, { payload }: PayloadAction<AddTabsPayload>) {
      const { workAreaId, tabs } = payload
      workAreaAdapter.updateOne(state, { id: workAreaId, changes: { tabs } })
    },
    setActiveWorkArea(state, { payload }: PayloadAction<WorkAreaId>) {
      state.activeWorkAreaId = payload
    },
    setActiveTabIdInWorkArea(state, { payload }: PayloadAction<SetActiveTabPayload>) {
      const { workAreaId, tabId } = payload
      workAreaAdapter.updateOne(state, { id: workAreaId, changes: { activeTabId: tabId } })
    },
    setTabDomain(state, { payload }: PayloadAction<SetTabDomainPayload>) {
      const workAreaState = state.entities[payload.workAreaId]
      const tab = workAreaState?.tabs.find(({ id }) => id === payload.tabId)
      if (tab) {
        tab.domainType = payload.domainType
      }
    },
    deleteWorkArea(state, payload: PayloadAction<WorkAreaId>) {
      workAreaAdapter.removeOne(state, payload)
    },
    clearAll() {
      return initialState
    },
    updateTreeElementStates(state, { payload: newTabElements }: PayloadAction<TabElementInfo[]>) {
      //TODO сделать рефакторинг без current
      const allAreas = Object.values(current(state).entities)
      for (const newTabElement of newTabElements) {
        const areaId = allAreas.find(area =>
          area?.tabs.some(tab => tab.id === newTabElement.tabId),
        )?.id
        if (!areaId) continue

        const tab = state.entities[areaId]?.tabs.find(tab => tab.id === newTabElement.tabId)
        if (!tab) continue

        switch (newTabElement.state) {
          case 'Unchecked': {
            tab.context = tab.context.filter(ctx => ctx.treeId !== newTabElement.projectElementId)
            tab.halfContext = tab.halfContext.filter(
              ctx => ctx.treeId !== newTabElement.projectElementId,
            )
            break
          }
          case 'Indeterminate': {
            tab.context = tab.context.filter(ctx => ctx.treeId !== newTabElement.projectElementId)
            if (tab.halfContext.every(ctx => ctx.treeId !== newTabElement.projectElementId)) {
              tab.halfContext.push(getContextObject(newTabElement))
            }
            break
          }
          case 'Checked': {
            tab.halfContext = tab.halfContext.filter(
              ctx => ctx.treeId !== newTabElement.projectElementId,
            )
            if (tab.context.every(ctx => ctx.treeId !== newTabElement.projectElementId)) {
              tab.context.push(getContextObject(newTabElement))
            }
            break
          }
        }
      }
    },
  },
})

const getContextObject = (newTabElement: TabElementInfo): ContextObject => {
  return {
    entityType: newTabElement.elementType,
    entityId: newTabElement.projectEntityId,
    treeId: newTabElement.projectElementId,
    entityName: newTabElement.elementName,
    customProperties: {
      fillColor: newTabElement.elementSettings.find(elem => elem.propertyType === 'FillColor')
        ?.value,
      strokeColor: newTabElement.elementSettings.find(elem => elem.propertyType === 'StrokeColor')
        ?.value,
      fontSize: newTabElement.elementSettings.find(elem => elem.propertyType === 'FontSize')?.value,
    },
  }
}

export default workAreaSlice.reducer
